import { NgModule, APP_INITIALIZER } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AccountModule } from './account/account.module';
import { AppRoutingModule } from './app-routing.module';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { AppComponent } from './app.component';
import {
  HttpClientModule,
  withInterceptors,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { localeInterceptor } from './core/interceptors/locale.interceptor';
import { mockInterceptor } from './core/interceptors/mock.interceptor';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { LayoutComponent } from './layout/layout.component';
import { ComponentsModule } from './components/components.module';
import { LanguageService } from './core/services/language.service';
import { HotelSelectionDialogComponent } from './components/hotel-selection-dialog/hotel-selection-dialog.component';
import { CardValidityDialogComponent } from './components/card-validity-dialog/card-validity-dialog.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import {
  HOTEL_SERVICE_TOKEN,
  HotelService,
} from './core/services/hotel.service';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { GoogleMapsModule } from '@angular/google-maps';

@NgModule({
  declarations: [
    AppComponent,
    LayoutComponent,
    HotelSelectionDialogComponent,
    CardValidityDialogComponent
  ],
  imports: [
    TranslateModule.forRoot({
      fallbackLang: 'en',
    }),
    FormsModule,
    ReactiveFormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    NgbModule,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatDialogModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    HttpClientModule,
    ComponentsModule,
    AccountModule,
    MatButtonModule,
    NgxDaterangepickerMd.forRoot(),
    DragDropModule,
    GoogleMapsModule,
  ],
  providers: [
    provideHttpClient(
      withInterceptors([mockInterceptor, authInterceptor, localeInterceptor, errorInterceptor]),
      withInterceptorsFromDi()
    ),
    provideAnimationsAsync(),
    // useHttpBackend bypasses HTTP interceptors. Translation files are static
    // assets and don't need auth/locale headers. Without this, an expired
    // token in the cookie causes the auth interceptor to short-circuit the
    // /assets/i18n/{lang}.json fetch with EMPTY, which makes the APP_INITIALIZER's
    // lastValueFrom() throw "EmptyError: no elements in sequence" on bootstrap.
    ...provideTranslateHttpLoader({ prefix: 'assets/i18n/', suffix: '.json', useHttpBackend: true }),
    // Pre-load the user's persisted language before guards run, so that
    // translate.instant() / translate.get() never return raw keys on first
    // navigation AND the very first translations request is for the right
    // locale (avoids loading en just to swap to fr a tick later).
    //
    // Depending on LanguageService here also forces it to be constructed
    // up-front, before any HTTP interceptor needs to inject it — which
    // matters because the locale interceptor's `inject(LanguageService)`
    // would otherwise create the service from inside the HTTP chain and
    // re-entrantly call translate.use() from there (see comments in
    // LanguageService constructor).
    {
      provide: APP_INITIALIZER,
      useFactory:
        (translate: TranslateService, languageService: LanguageService) =>
        () =>
          lastValueFrom(translate.use(languageService.getCurrentLanguage())),
      deps: [TranslateService, LanguageService],
      multi: true,
    },
    { provide: HOTEL_SERVICE_TOKEN, useClass: HotelService },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
